import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import { 
  FileEdit, 
  Save, 
  Plus, 
  Trash2, 
  Loader2,
  Search,
  RefreshCw,
  Eye,
  ArrowLeft,
  Home,
  BookOpen,
  Library,
  MessageSquare,
  Heart,
  LayoutGrid,
  Crown
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

const PAGE_ICONS = {
  landing: Home,
  home: Home,
  browse: Search,
  series: BookOpen,
  library: Library,
  confessions: MessageSquare,
  prazer: Heart,
  premium: Crown,
  common: LayoutGrid
};

const PAGE_LABELS = {
  landing: 'Página Inicial (Landing)',
  home: 'Home (Autenticado)',
  browse: 'Explorar',
  series: 'Séries',
  library: 'Biblioteca',
  confessions: 'Confissões',
  prazer: '+ Prazer',
  premium: 'Página Premium',
  common: 'Elementos Comuns'
};

export default function AdminContentEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contentBlocks, setContentBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPage, setSelectedPage] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [editedContent, setEditedContent] = useState({});
  
  // New block form
  const [newBlock, setNewBlock] = useState({
    key: '',
    content: '',
    content_type: 'text',
    page: 'landing',
    section: '',
    label: ''
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadContent();
  }, [user, navigate]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cms/content');
      setContentBlocks(res.data);
      
      // Initialize edited content state
      const initialState = {};
      res.data.forEach(block => {
        initialState[block.key] = block.content;
      });
      setEditedContent(initialState);
    } catch (error) {
      toast.error('Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedContent = async () => {
    try {
      const res = await api.post('/admin/cms/seed');
      toast.success(`${res.data.created} blocos criados, ${res.data.skipped} já existiam`);
      loadContent();
    } catch (error) {
      toast.error('Erro ao criar conteúdo padrão');
    }
  };

  const handleContentChange = (key, value) => {
    setEditedContent(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveBlock = async (key) => {
    setSaving(true);
    try {
      await api.put(`/admin/cms/content/${key}`, {
        content: editedContent[key]
      });
      toast.success('Conteúdo guardado');
      loadContent();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    if (!newBlock.key || !newBlock.content || !newBlock.label) {
      toast.error('Preenche todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/cms/content', newBlock);
      toast.success('Bloco criado');
      setCreateDialogOpen(false);
      setNewBlock({ key: '', content: '', content_type: 'text', page: 'landing', section: '', label: '' });
      loadContent();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao criar bloco'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlock = async (key) => {
    if (!confirm('Eliminar este bloco de conteúdo?')) return;
    
    try {
      await api.delete(`/admin/cms/content/${key}`);
      toast.success('Bloco eliminado');
      loadContent();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  // Group content by page
  const groupedContent = contentBlocks.reduce((acc, block) => {
    const page = block.page || 'other';
    if (!acc[page]) acc[page] = [];
    acc[page].push(block);
    return acc;
  }, {});

  // Filter content
  const filteredContent = Object.entries(groupedContent).reduce((acc, [page, blocks]) => {
    if (selectedPage !== 'all' && page !== selectedPage) return acc;
    
    const filtered = blocks.filter(block => 
      block.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      block.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      block.content.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (filtered.length > 0) {
      acc[page] = filtered;
    }
    return acc;
  }, {});

  const pages = [...new Set(contentBlocks.map(b => b.page))];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-ruby/20 flex items-center justify-center">
                <FileEdit className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Editor de Conteúdo</h1>
                <p className="text-muted-foreground">Edita os textos das páginas da plataforma</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleSeedContent}
              className="border-border"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Criar Padrão
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-ruby hover:bg-ruby-light">
              <Plus className="w-4 h-4 mr-2" />
              Novo Bloco
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-surface border-border mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar conteúdo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-background border-border"
                />
              </div>
              <Select value={selectedPage} onValueChange={setSelectedPage}>
                <SelectTrigger className="w-full sm:w-[200px] bg-background border-border">
                  <SelectValue placeholder="Filtrar por página" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Páginas</SelectItem>
                  {pages.map(page => (
                    <SelectItem key={page} value={page}>
                      {PAGE_LABELS[page] || page}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Content Blocks */}
        {Object.keys(filteredContent).length === 0 ? (
          <Card className="bg-surface border-border">
            <CardContent className="py-16 text-center">
              <FileEdit className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">
                {contentBlocks.length === 0 ? 'Nenhum conteúdo criado' : 'Nenhum resultado encontrado'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {contentBlocks.length === 0 
                  ? 'Clica em "Criar Padrão" para criar os blocos de conteúdo iniciais'
                  : 'Tenta outros termos de pesquisa'
                }
              </p>
              {contentBlocks.length === 0 && (
                <Button onClick={handleSeedContent} className="bg-ruby hover:bg-ruby-light">
                  Criar Conteúdo Padrão
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Accordion type="multiple" defaultValue={Object.keys(filteredContent)} className="space-y-4">
            {Object.entries(filteredContent).map(([page, blocks]) => {
              const PageIcon = PAGE_ICONS[page] || LayoutGrid;
              return (
                <AccordionItem key={page} value={page} className="bg-surface border border-border rounded-lg overflow-hidden">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-white/5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-ruby/20">
                        <PageIcon className="w-4 h-4 text-ruby" />
                      </div>
                      <span className="font-semibold text-foreground">{PAGE_LABELS[page] || page}</span>
                      <Badge variant="outline" className="border-border ml-2">
                        {blocks.length} blocos
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="space-y-4 pt-2">
                      {blocks.map(block => (
                        <div key={block.key} className="p-4 bg-white/5 rounded-lg">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                              <h4 className="font-medium text-foreground">{block.label}</h4>
                              <p className="text-xs text-muted-foreground font-mono">{block.key}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSaveBlock(block.key)}
                                disabled={saving || editedContent[block.key] === block.content}
                                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                Guardar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteBlock(block.key)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          {block.content_type === 'html' ? (
                            <RichTextEditor
                              content={editedContent[block.key] || ''}
                              onChange={(value) => handleContentChange(block.key, value)}
                              placeholder="Conteúdo HTML…"
                            />
                          ) : (
                            <Textarea
                              value={editedContent[block.key] || ''}
                              onChange={(e) => handleContentChange(block.key, e.target.value)}
                              className="bg-background border-border min-h-[80px] font-medium"
                              placeholder="Conteúdo..."
                            />
                          )}
                          {editedContent[block.key] !== block.content && (
                            <p className="text-xs text-gold mt-2">* Alterações não guardadas</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-lg z-50">
            <DialogHeader>
              <DialogTitle className="text-foreground">Novo Bloco de Conteúdo</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateBlock} className="space-y-4">
              <div className="space-y-2">
                <Label>Página *</Label>
                <Select 
                  value={newBlock.page} 
                  onValueChange={(v) => setNewBlock({...newBlock, page: v})}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAGE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Secção</Label>
                <Input
                  value={newBlock.section}
                  onChange={(e) => setNewBlock({...newBlock, section: e.target.value})}
                  placeholder="Ex: hero, footer, features"
                  className="bg-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Chave (identificador único) *</Label>
                <Input
                  value={newBlock.key}
                  onChange={(e) => setNewBlock({...newBlock, key: e.target.value})}
                  placeholder="Ex: landing.hero.title"
                  className="bg-background border-border font-mono"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Formato: página.secção.elemento
                </p>
              </div>
              <div className="space-y-2">
                <Label>Nome (visível no admin) *</Label>
                <Input
                  value={newBlock.label}
                  onChange={(e) => setNewBlock({...newBlock, label: e.target.value})}
                  placeholder="Ex: Título Principal"
                  className="bg-background border-border"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Conteúdo</Label>
                <Select
                  value={newBlock.content_type}
                  onValueChange={(v) => setNewBlock({...newBlock, content_type: v})}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texto simples</SelectItem>
                    <SelectItem value="html">HTML / Rich Text</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Conteúdo *</Label>
                {newBlock.content_type === 'html' ? (
                  <RichTextEditor
                    content={newBlock.content}
                    onChange={(value) => setNewBlock({...newBlock, content: value})}
                    placeholder="Conteúdo HTML…"
                  />
                ) : (
                  <Textarea
                    value={newBlock.content}
                    onChange={(e) => setNewBlock({...newBlock, content: e.target.value})}
                    placeholder="O texto que será exibido..."
                    className="bg-background border-border min-h-[100px]"
                    required
                  />
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-border">
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-ruby hover:bg-ruby-light">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Criar Bloco
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
